var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Button } from '../button';
import { Card } from '../card';
import { getFileType, uploadFile } from './file-helpers';
import { Attachment, Settings } from '@otp/icons';
import styles from './file-uploader.module.css';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
export const FileUploader = ({ onUploadFiles, getUploadParams, onFileUploaded, buttonContent: ButtonContent, buttonFullWidth, fileDescription, fileAccept, error, showLoader = true, maxFileCount, }) => {
    const { t } = useTranslation();
    const hiddenFileInput = useRef(null);
    const [loadingFilesNo, setLoadingFilesNo] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const isLoading = loadingFilesNo > 0;
    const openFileModal = () => {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.click();
        }
    };
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDrop = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0)
            return;
        if (onUploadFiles) {
            // Create a synthetic event to match the input onChange event
            const syntheticEvent = {
                target: { files: e.dataTransfer.files },
                preventDefault: () => { },
                stopPropagation: () => { },
            };
            onUploadFiles(syntheticEvent);
            return;
        }
        if (!getUploadParams || !onFileUploaded) {
            throw new Error('getUploadParams and onFileUploaded are required');
        }
        setLoadingFilesNo((prev) => prev + files.length);
        files.forEach((file) => __awaiter(void 0, void 0, void 0, function* () {
            const { uploadUrl, getUrl, key, method } = yield getUploadParams({
                fileName: file.name,
            });
            if (!uploadUrl) {
                return;
            }
            const response = yield uploadFile(file, uploadUrl, method);
            setLoadingFilesNo((prev) => prev - 1);
            if (response.ok) {
                onFileUploaded({
                    key,
                    url: getUrl,
                    title: file.name,
                    type: getFileType(file),
                });
            }
        }));
    });
    const uploadFiles = (event) => __awaiter(void 0, void 0, void 0, function* () {
        if (onUploadFiles) {
            onUploadFiles(event);
            return;
        }
        if (!getUploadParams || !onFileUploaded) {
            throw new Error('getUploadParams and onFileUploaded are required');
        }
        const files = Array.from(event.target.files || []);
        setLoadingFilesNo((prev) => prev + files.length);
        files.forEach((file) => __awaiter(void 0, void 0, void 0, function* () {
            const { uploadUrl, getUrl, key, method } = yield getUploadParams({
                fileName: file.name,
            });
            if (!uploadUrl) {
                return;
            }
            const response = yield uploadFile(file, uploadUrl, method);
            setLoadingFilesNo((prev) => prev - 1);
            if (response.ok) {
                onFileUploaded({
                    key,
                    url: getUrl,
                    title: file.name,
                    type: getFileType(file),
                });
            }
        }));
    });
    const ButtonContentComponent = ButtonContent && typeof ButtonContent === 'function' ? (_jsx(ButtonContent, { disabled: isLoading, loading: isLoading })) : (ButtonContent);
    return (_jsxs(_Fragment, { children: [fileDescription !== false && (_jsx(Card, { className: clsx(styles.card), children: fileDescription ||
                    t('file-uploader.text-description', 'You can add a pdf, image, video, or a document.') })), _jsx(Box, { onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop, className: clsx(styles.dropZone, {
                    [styles.dragging]: isDragging,
                }), children: _jsxs(Button, { variant: "outlined", fullWidth: buttonFullWidth, onClick: openFileModal, disabled: isLoading, className: clsx(styles.uploadButton, { 'border-red-500': error }), children: [ButtonContentComponent || (_jsxs(_Fragment, { children: [_jsx(Attachment, {}), "\u00A0 ", t('file-uploader.add-attachments', '+Add Attachments')] })), isLoading && showLoader && (_jsx(Settings, { className: clsx(styles.spinner) }))] }) }), _jsx("input", { type: "file", accept: fileAccept === null || fileAccept === void 0 ? void 0 : fileAccept.join(','), multiple: maxFileCount ? maxFileCount > 1 : true, onChange: uploadFiles, ref: hiddenFileInput, style: { display: 'none' } })] }));
};
